import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Lock, ArrowRight, CreditCard } from "lucide-react";

const CARECREDIT_URL = "https://www.carecredit.com/apply/";

/**
 * Reusable CareCredit financing banner used across Home, Fees, Services, Location
 * pages, Group Immersion, and Disorganized Attachment pages. One source of truth
 * for copy & styling so every placement stays consistent.
 *
 * Variants:
 *  - "default" : soft cream background, works on white or neutral pages
 *  - "muted"   : subtle primary-tinted background for pages with cream sections
 */
const CareCreditBanner = ({ variant = "default", className = "" }) => {
  const bg =
    variant === "muted"
      ? "bg-gradient-to-br from-secondary/40 via-white to-primary/5"
      : "bg-gradient-to-br from-primary/5 via-white to-accent/10";

  return (
    <section
      className={`relative overflow-hidden px-6 md:px-12 py-16 md:py-20 ${bg} ${className}`}
      data-testid="carecredit-banner"
    >
      {/* soft decorative blobs */}
      <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-accent/15 blur-3xl pointer-events-none" />

      <div className="relative max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl bg-white/90 backdrop-blur border border-primary/15 shadow-soft overflow-hidden"
        >
          <div className="grid grid-cols-1 lg:grid-cols-5 items-stretch">
            {/* Left: copy */}
            <div className="lg:col-span-3 px-6 md:px-10 py-8 md:py-12">
              <span
                className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em]"
                data-testid="carecredit-banner-badge"
              >
                <CreditCard className="h-3.5 w-3.5" />
                Flexible Financing · HIPAA-Compliant
              </span>

              <h2 className="mt-5 font-serif text-3xl sm:text-4xl md:text-[2.6rem] font-semibold text-foreground leading-tight">
                Start Your Healing Today —{" "}
                <span className="italic text-primary">Pay Monthly With CareCredit</span>
              </h2>

              <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
                We&rsquo;ve partnered with{" "}
                <strong className="text-foreground">CareCredit</strong>, a
                HIPAA-compliant health-related financing provider. Apply today to
                have your full intensive payment covered while making manageable,
                low monthly payments.
              </p>

              <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4">
                <a
                  href={CARECREDIT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    size="lg"
                    className="rounded-full px-7 py-6 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-semibold w-full sm:w-auto"
                    data-testid="carecredit-banner-cta"
                  >
                    Apply for CareCredit Financing
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
                <p className="text-xs text-muted-foreground max-w-[18rem]">
                  Opens in a new tab. Application takes less than 5 minutes.
                </p>
              </div>
            </div>

            {/* Right: trust column */}
            <div className="lg:col-span-2 bg-gradient-to-br from-primary via-primary to-primary/90 text-primary-foreground px-6 md:px-10 py-8 md:py-12 flex flex-col justify-center">
              <p className="text-xs uppercase tracking-[0.22em] font-semibold text-accent">
                Safe · Confidential · Secure
              </p>
              <ul className="mt-5 space-y-4 text-sm md:text-base">
                <li className="flex items-start gap-3" data-testid="carecredit-trust-soft-pull">
                  <ShieldCheck className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <span>
                    <strong className="block text-primary-foreground">
                      Only a soft credit pull
                    </strong>
                    <span className="text-primary-foreground/85">
                      Checking your rate will not impact your credit score.
                    </span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Lock className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <span>
                    <strong className="block text-primary-foreground">
                      HIPAA-compliant &amp; encrypted
                    </strong>
                    <span className="text-primary-foreground/85">
                      Your health &amp; financial information stays private.
                    </span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CreditCard className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <span>
                    <strong className="block text-primary-foreground">
                      Low monthly payments
                    </strong>
                    <span className="text-primary-foreground/85">
                      Spread the cost of your intensive over time.
                    </span>
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CareCreditBanner;
